import { ref } from 'vue';

import { Nullable } from '@/types/common';
import { MicGraph, MicGraphSetupOptions, MicProcessEvents } from '@/types/microphone';
import { BUFFER_SIZE, MIC_INITIAL_GAIN, SAMPLE_RATE } from '@/utils/constants/microphone';

const PROCESSOR_NAME = 'audio-processor';

const resolveProcessorPath = (): string => {
  const base = import.meta.env.BASE_URL;
  return base === '/' ? '/js/processor.js' : `${base}js/processor.js`;
};

export const useMicGraph = () => {
  const micGraph = ref<Nullable<MicGraph>>(null);

  const setupMicGraph = async (options: MicGraphSetupOptions): Promise<void> => {
    try {
      if (!options.stream.value) {
        throw new Error();
      }

      const context = new AudioContext({ sampleRate: SAMPLE_RATE });
      await context.audioWorklet.addModule(resolveProcessorPath());

      const graph: MicGraph = {
        context,
        sourceNode: context.createMediaStreamSource(options.stream.value),
        gain: context.createGain(),
        analyser: context.createAnalyser(),
        processor: new AudioWorkletNode(context, PROCESSOR_NAME, {
          processorOptions: { bufferSize: BUFFER_SIZE },
        }),
      };

      graph.gain.gain.value = MIC_INITIAL_GAIN;
      graph.analyser.fftSize = 1024;

      graph.sourceNode
        .connect(graph.gain)
        .connect(graph.analyser)
        .connect(graph.processor)
        .connect(graph.context.destination);

      micGraph.value = graph;

      attachMicProcessor({
        onmessage: options.events.onmessage,
        onmessageerror: options.events.onmessageerror,
      });
    } catch {
      throw new Error();
    }
  };

  const teardownMicGraph = async (): Promise<void> => {
    if (!micGraph.value) return;

    const graph = micGraph.value;

    graph.processor.port.close();
    graph.processor.disconnect();
    graph.sourceNode.disconnect();
    graph.gain.disconnect();
    graph.analyser.disconnect();

    if (graph.context.state !== 'closed') {
      try {
        await graph.context.close();
      } catch (e) {
        console.error(e);
      }
    }

    micGraph.value = null;
  };

  const attachMicProcessor = (events: MicProcessEvents) => {
    if (!micGraph.value) {
      return;
    }
    if (events.onmessage) {
      micGraph.value.processor.port.onmessage = events.onmessage;
    }
    if (events.onmessageerror) {
      micGraph.value.processor.port.onmessageerror = events.onmessageerror;
    }
  };

  return {
    micGraph,
    setupMicGraph,
    teardownMicGraph,
  };
};
